import { Injectable } from '@angular/core';
import {Observable, ReplaySubject, of, firstValueFrom} from 'rxjs';
import {environment} from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import {map, tap} from 'rxjs/operators';
import {StateManagerService} from './state-manager.service';
import {User} from "../models/user";

@Injectable()
export class ProductsService {

  private baseUrl = environment.baseUrls().api;
  private cmsBaseUrl = this.baseUrl + 'cms/';

  constructor(private http: HttpClient,
              private stateManager: StateManagerService) {
    this.stateManager.setPrefix('PROD');
  }

  private static cache:any = {};
  get(type: string, activeOnly: boolean): Promise<any> {
    if (!ProductsService.cache[type]) {
      const o = this.stateManager.getStateKeyValue('get-' + type);
      ProductsService.cache[type] = firstValueFrom(
        this.stateManager.hasValue(o.val) ? of(o.val) :
        this.http
          .get<any>(this.baseUrl + 'products/' + type + (activeOnly ? '?activeOnly=true' : ''))
          .pipe(tap(result => this.stateManager.setStateValue(o.key, result)))
      );
    }

    return ProductsService.cache[type];
  }

  discounts(qtys: Array<number>, productTypes: Array<string>): Promise<any> {
    const key = qtys.join(',') + '/' + productTypes.join(',');
    if (!ProductsService.cache[key]) {
      const o = this.stateManager.getStateKeyValue('discounts-' + key);

      if (this.stateManager.hasValue(o.val)) { return firstValueFrom(of(o.val)); }
      ProductsService.cache[key] = firstValueFrom(
        this.stateManager.hasValue(o.val) ? of(o.val) :
        this.http
          .get(this.baseUrl + 'products/discount/' + key)
          .pipe(tap(result => this.stateManager.setStateValue(o.key, result)))
      );
    }

    return ProductsService.cache[key];
  }


  categories(qty:number, type:string): Promise<any> {
    const o = this.stateManager.getStateKeyValue('categories-' + qty + '-' + type);
    if (this.stateManager.hasValue(o.val)) { return o.val; }
    return firstValueFrom(
      this.stateManager.hasValue(o.val) ? of(o.val) :
      this.http
        .get(this.baseUrl + 'products/categories/' + qty + '/' + type)
        .pipe(tap( result => this.stateManager.setStateValue(o.key, result) ))
    );
  }

  matchItemByTags(item, tags):boolean {

    let match:boolean = true;

    if (tags) {
      for (let key in tags) {
        let val = typeof item[key] !== 'undefined' ? item[key].toString().toLowerCase() : null;
        match = match && typeof tags[key] !== 'undefined' ? tags[key].toString().toLowerCase().split(',').indexOf(val) >= 0 : false;
      }
    }

    return match;
  }

  filterByTags(data, tags): Array<any> {
    if (!tags) {
      return data;
    }

    return data.filter( item => {
      return this.matchItemByTags(item, tags);
    });
  }

  getTags(options: any) {
    // calculate the tags
    const tags = {};
    options.forEach( option => {
      if (option.selection) {
        tags[option.tagName] = option.selection;
      }
    });
    return tags;
  }

  predefinedProduct(id: string) {
    if (id) {
      const o = this.stateManager.getStateKeyValue('predefinedProduct-' + id);
      return firstValueFrom(
        this.stateManager.hasValue(o.val) ? of(o.val) :
        this.http
          .get(this.cmsBaseUrl + 'product?id=' + id)
          .pipe(
            map( (result: any) => {
              const retValue = {
                projectData: result.projectData ? {
                  outerSize: result.projectData.outerSize,
                  openings: result.projectData.openings ? result.projectData.openings : null,
                  qty: result.project ? result.project.qty : 1,
                  topMat: result.projectData.topMat ? result.projectData.topMat : result.projectData.mats ? result.projectData.mats[0][0] : null,
                  bottomMat: result.projectData.bottomMat ? result.projectData.bottomMat : result.projectData.mats && result.projectData.mats[1] ? result.projectData.mats[1][0] : null,
                  moulding: result.projectData.moulding ? result.projectData.moulding : null,
                  glazing: result.projectData.glazing ? result.projectData.glazing : null,
                  backingBoard: result.projectData.backingBoard ? result.projectData.backingBoard : null,
                  bag: result.projectData.bag ? result.projectData.bag : null,
                  design: result.projectData.design ? result.projectData.design : (result.projectData.designId ? {id: result.projectData.designId} : null),
                  permanentDesignId: result.projectData.permanentDesignId,
                  complexityUpCharge: result.projectData.complexityUpCharge,
                  reveal: result.projectData.reveal,
                  primaryProduct: result.projectData.primaryProduct,
                  restrictions: result.projectData.restrictions,
                  print: result.projectData.print,
                  seoData: {
                    metaTitle: result.pageTitle ? result.pageTitle : result.name,
                    metaDescription: result.metaDescription ? result.metaDescription : (result.description ? result.description : result.name),
                  }
                } : null
              };
              this.stateManager.setStateValue(o.key, retValue);
              return retValue;
            })
          )
        );
    } else {
      return firstValueFrom(of(null));
    }
  }
}
